import { useAuthStore } from '@features/auth/store/authStore';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { addDoc, collection, deleteDoc, doc, getDoc, onSnapshot, orderBy, query, Timestamp, where } from 'firebase/firestore';
import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import notificationSound from '../../assets/sounds/notification.mp3';
import { db } from '../../config/firebase';
import api from '../services/api';
import { Notification, NotificationsResponse, NotificationType } from '../types/notification.types';
import { useSound } from './useSound';

export const notificationEndpoints = {
  getAll: '/notifications',
  markAsRead: (id: string) => `/notifications/${id}/read`,
  markAllAsRead: '/notifications/read-all',
  delete: (id: string) => `/notifications/${id}`,
  create: '/notifications',
};

const PAGE_SIZE = 5;

interface PageParam {
  lastId?: string | null;
}

export const useNotifications = () => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { play: playNotificationSound } = useSound(notificationSound, { volume: 0.3 });
  const lastNotification = useRef<Notification>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ['notifications'] as const,
    queryFn: async ({ pageParam = { lastId: null } }: { pageParam?: PageParam }) => {
      const response = await api.get<NotificationsResponse>(notificationEndpoints.getAll, {
        params: {
          lastId: pageParam.lastId,
          limit: PAGE_SIZE,
        },
      });
      return response.data;
    },
    getNextPageParam: (lastPage): PageParam | undefined => {
      if (!lastPage.hasMore) return undefined;
      return { lastId: lastPage.notifications[lastPage.notifications.length - 1]?.id };
    },
    initialPageParam: { lastId: null } as PageParam,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled: !!user?.uid, // Only run query if user is authenticated
  });

  // Listen to realtime updates
  useEffect(() => {
    if (!user?.uid) return;

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      where('read', '==', false),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const nonMessageNotifications = snapshot.docs.filter(
        (doc) => (doc.data() as Notification).type !== NotificationType.NEW_MESSAGE
      );
      if (nonMessageNotifications.length > 0) {
        lastNotification.current = nonMessageNotifications[0].data() as Notification;
        playNotificationSound();
      }

      /* queryClient.setQueryData(['notifications'], (old: any) => {
        if (!old?.pages?.[0]) return old;
        return {
        ...old,
        pages: old.pages.map((page: NotificationsResponse, index: number) =>
          index === 0 ? { ...page, unreadCount: snapshot.size } : page
        ),
        };
      }); */
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    });

    return () => unsubscribe();
  }, [user?.uid, queryClient, playNotificationSound]);

  // Notification listeners for custom events
  const notificationListeners = useRef<((notification: Notification) => void)[]>([]);

  const addNotificationListener = (listener: (notification: Notification) => void) => {
    notificationListeners.current.push(listener);
  };

  const removeNotificationListener = (listener: (notification: Notification) => void) => {
    notificationListeners.current = notificationListeners.current.filter(l => l !== listener);
  };

  // Notify listeners when a new notification is received
  useEffect(() => {
    if (lastNotification.current) {
      notificationListeners.current.forEach(listener => {
        listener(lastNotification.current);
      });
    }
  }, [lastNotification.current]);

  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      await api.patch(notificationEndpoints.markAsRead(notificationId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      await api.patch(notificationEndpoints.markAllAsRead);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const deleteNotificationMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      try {
        // Delete from Firebase
        await deleteDoc(doc(db, 'notifications', notificationId));

        // Update local cache
        queryClient.setQueryData(['notifications'], (old: any) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              notifications: page.notifications.filter((n: Notification) => n.id !== notificationId),
            })),
          };
        });
      } catch (error) {
        console.error('Error deleting notification:', error);
        throw error;
      }
    },
    onSuccess: () => {
      toast.success('Notification deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAsRead = async (notificationId: string) => {
    await markAsReadMutation.mutateAsync(notificationId);
  };

  const markAllAsRead = async () => {
    await markAllAsReadMutation.mutateAsync();
  };

  const deleteNotification = async (notificationId: string) => {
    await deleteNotificationMutation.mutateAsync(notificationId);
  };

  // Import the notification templates - for reference only
  // Proper implementation is in features/notifications/hooks/useNotifications.ts
  // which we will use as a reference

  /**
   * Create a notification
   * @param notification Notification data to create
   * @returns Promise resolving to true if successful, false otherwise
   */
  interface CreateNotificationParams {
    receiverId: string;
    type: NotificationType;
    metadata?: {
      actorId?: string;
      [key: string]: any;
    };
  }

  // Custom notification for groups (internal)
  const createGroupNotification = async ({
    receiverId,
    actorId,
    groupId,
    groupTitle
  }: {
    receiverId: string;
    actorId: string;
    groupId: string;
    groupTitle: string;
  }) => {
    try {
      let senderName = 'Someone';
      const senderDoc = await getDoc(doc(db, 'users', actorId));
      if (senderDoc.exists()) {
        const senderData = senderDoc.data();
        senderName = senderData.name || senderData.username || 'Someone';
      }
      const notification = {
        userId: receiverId,
        type: NotificationType.GROUP_ADDED,
        title: 'Added to group',
        message: `${senderName} added you to the group ${groupTitle}`,
        priority: 'normal',
        data: {
          actorId,
          groupId,
          title: groupTitle,
        },
        read: false,
        createdAt: Timestamp.now(),
      };
      await addDoc(collection(db, 'notifications'), notification);
      return true;
    } catch (error) {
      console.error('Error creating group notification:', error);
      throw error;
    }
  };

  const createNotification = async ({
    receiverId,
    type,
    metadata = {}
  }: CreateNotificationParams) => {
    try {
      // Get sender's name if available
      let senderName = 'Someone';
      if (metadata.actorId) {
        const senderDoc = await getDoc(doc(db, 'users', metadata.actorId));
        if (senderDoc.exists()) {
          const senderData = senderDoc.data();
          senderName = senderData.name || senderData.username || 'Someone';
        }
      }

      // Create basic notification
      const notification = {
        userId: receiverId,
        type,
        title: type === NotificationType.SUPPORT_MESSAGE ? 'Support Message' : 'New Message',
        message: type === NotificationType.SUPPORT_MESSAGE ?
          `${senderName} sent you a support message` :
          `${senderName} sent you a new message`,
        priority: type === NotificationType.SUPPORT_MESSAGE ? 'high' : 'normal',
        data: {
          ...metadata,
          actorId: metadata.actorId,
        },
        read: false,
        createdAt: Timestamp.now(),
      };

      await addDoc(collection(db, 'notifications'), notification);
      return true;
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  };

  const allNotifications = data?.pages.flatMap(page => page.notifications) || [];
  const totalUnreadCount = data?.pages[0]?.unreadCount || 0;

  return {
    notifications: allNotifications,
    unreadCount: totalUnreadCount,
    loading: isLoading,
    loadMore: fetchNextPage,
    hasMore: hasNextPage,
    loadingMore: isFetchingNextPage,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    lastNotification,
    createNotification,
    createGroupNotification,
    addNotificationListener,
    removeNotificationListener,
  };
};
